import { useEffect, useState } from 'react';
import { InputNumber, Select } from 'antd';
import type { PosServiceLineKind } from '@tcr-erp/shared';
import { formatNumber } from '@/utils/format';
import { MONEY_MAX, moneyFormatter, moneyParser, moneyInputKeyDownGuard } from '../utils/moneyInput';

/**
 * Component dùng chung cho CẢ dòng "Phụ thu" lẫn dòng "Phí vận chuyển" ở màn
 * POS: nhãn bên trái là dropdown chọn dịch vụ (mang luôn thuế suất đang áp),
 * ô tiền bên phải LUÔN sửa được. Trước khi có component này, hai dòng chép
 * tay riêng — đó là chỗ hai bản trôi lệch nhau (Task 17).
 */
export interface ServiceOption {
  id: string;
  name: string;
  /** `null` = dịch vụ chưa khai giá (ô tiền phải để TRỐNG). `0` = giá 0 cố ý (điền số 0). */
  unitPrice: number | null;
  /** `null` = dịch vụ chưa khai thuế suất riêng, dùng `defaultVatRate` của dòng. */
  vatRate: number | null;
  /** VAT đã resolve qua VatResolverService (TCR-1362) khi dịch vụ không tự khai vatRate — ưu tiên thấp hơn vatRate riêng, cao hơn defaultVatRate của dòng. */
  resolvedVatRate?: number | null;
}

export interface ServiceFeeValue {
  serviceId: string | null;
  amount: number;
}

export interface ServiceFeeRowProps {
  kind: PosServiceLineKind;
  label: string;
  options: ServiceOption[];
  defaultVatRate: number;
  value: ServiceFeeValue;
  onChange: (v: ServiceFeeValue) => void;
  /**
   * `options` đang tải LẦN ĐẦU (chưa có cache) — Select hiện skeleton loading
   * thay vì Select thật. Tránh tình huống `value.serviceId` đã có (từ form)
   * nhưng `options` chưa kịp chứa nó ⇒ Select hiện trống, đọc như mất lựa
   * chọn (TCR-1104). Không ảnh hưởng ô tiền — ô tiền luôn sửa được.
   */
  optionsLoading?: boolean;
  /** Màn chi tiết đơn / sửa đơn: render text tĩnh, không có Select/InputNumber. */
  readOnly?: boolean;
  /** Chỉ dùng khi `readOnly` — snapshot của đơn, không tra lại `options`. */
  resolvedVatRate?: number;
  /** Chỉ dùng khi `readOnly` — snapshot của đơn, không tra lại `options`. */
  resolvedName?: string;
}

/**
 * Thuế suất HIỂN THỊ cho một dịch vụ, ưu tiên 3 tầng: `vatRate` riêng của
 * dịch vụ → `resolvedVatRate` đã resolve qua nhóm ngành thuế (TCR-1362
 * follow-up, khi dịch vụ không tự khai `vatRate`) → `defaultVatRate` của dòng
 * (kèm cờ `isDefault` để UI ghi rõ "(mặc định)") khi cả hai đều vắng.
 *
 * Export riêng để Task 18 (và `OrderInfoPanel` readOnly) dùng lại — KHÔNG để
 * mỗi nơi tự suy ra thuế suất theo cách riêng, vì đó chính là kiểu trôi lệch
 * mà việc gộp component này muốn tránh.
 */
export function resolveDisplayVatRate(
  serviceId: string | null,
  options: ServiceOption[],
  defaultVatRate: number,
): { rate: number; isDefault: boolean } {
  if (serviceId === null) return { rate: defaultVatRate, isDefault: true };
  const option = options.find((o) => o.id === serviceId);
  if (!option) return { rate: defaultVatRate, isDefault: true };
  if (option.vatRate !== null) return { rate: option.vatRate, isDefault: false };
  if (option.resolvedVatRate !== null && option.resolvedVatRate !== undefined) {
    return { rate: option.resolvedVatRate, isDefault: false };
  }
  return { rate: defaultVatRate, isDefault: true };
}

function buildPlaceholderLabel(label: string, defaultVatRate: number): string {
  return `${label} (VAT ${defaultVatRate}%)`;
}

/** Format hiển thị trong Ô ĐÃ CHỌN (Select đóng): "Tên dịch vụ | VAT X%" — QC round 6: đảo lại tên lên trước. */
function buildSelectedValueLabel(name: string, rate: number): string {
  return `${name} | VAT ${rate}%`;
}

/** Format hiển thị trong DANH SÁCH khi dropdown đang MỞ: "Tên dịch vụ (VAT X%)". */
function buildOptionListLabel(name: string, rate: number): string {
  return `${name} (VAT ${rate}%)`;
}

interface ServiceSelectOption {
  value: string;
  label: string;
  name: string;
  rate: number;
}

export function ServiceFeeRow({
  kind,
  label,
  options,
  defaultVatRate,
  value,
  onChange,
  optionsLoading = false,
  readOnly = false,
  resolvedVatRate,
  resolvedName,
}: ServiceFeeRowProps): React.JSX.Element {
  // Echo cục bộ của `value`: cho phép UI phản ứng ngay khi người dùng chọn
  // dịch vụ / gõ tiền, kể cả trước khi component cha kịp re-render với `value`
  // mới (`onChange` chỉ là thông báo ra ngoài, không đảm bảo quay lại ngay).
  // Đồng bộ lại từ props khi `value` bên ngoài đổi (ví dụ load đơn khác).
  const [local, setLocal] = useState<ServiceFeeValue>(value);

  useEffect(() => {
    setLocal(value);
  }, [value.serviceId, value.amount]);

  if (readOnly) {
    const rate = resolvedVatRate ?? defaultVatRate;
    const name = resolvedName ?? label;
    return (
      <td colSpan={2} data-testid={`service-fee-row-${kind}-readonly`}>
        <span>{`${name} (VAT ${rate}%)`}</span>
        <span style={{ float: 'right', fontWeight: 600 }}>{formatNumber(value.amount)}</span>
      </td>
    );
  }

  const selectedOption = options.find((o) => o.id === local.serviceId) ?? null;
  const { rate } = resolveDisplayVatRate(local.serviceId, options, defaultVatRate);
  const placeholderLabel = buildPlaceholderLabel(label, defaultVatRate);
  const selectLabel = selectedOption
    ? buildSelectedValueLabel(selectedOption.name, rate)
    : placeholderLabel;
  const serviceHasNoPrice = selectedOption ? selectedOption.unitPrice === null : false;
  const displayAmount = local.amount === 0 && serviceHasNoPrice ? null : local.amount;
  const selectListOptions: ServiceSelectOption[] = options.map((o) => {
    const optRate = o.vatRate ?? o.resolvedVatRate ?? defaultVatRate;
    return {
      value: o.id,
      label: buildSelectedValueLabel(o.name, optRate),
      name: o.name,
      rate: optRate,
    };
  });

  function handleSelectChange(id: string | undefined): void {
    if (id === undefined) {
      const next: ServiceFeeValue = { serviceId: null, amount: local.amount };
      setLocal(next);
      onChange(next);
      return;
    }
    const option = options.find((o) => o.id === id) ?? null;
    const next: ServiceFeeValue = { serviceId: id, amount: option?.unitPrice ?? 0 };
    setLocal(next);
    onChange(next);
  }

  function handleAmountChange(v: number | null): void {
    const next: ServiceFeeValue = { serviceId: local.serviceId, amount: v ?? 0 };
    setLocal(next);
    onChange(next);
  }

  return (
    <>
      {/* `width` ở đây chỉ mang tính tài liệu — bảng cha dùng `tableLayout:
          'fixed'` nên width THẬT SỰ áp dụng cho cột này lấy từ `LABEL` (dòng
          đầu tiên của bảng) trong `OrderInfoPanel.tsx`, không phải giá trị
          khai ở đây. Giữ 2 nơi cùng 1 số (78%) để đọc code không hiểu nhầm. */}
      <td data-testid={`service-fee-row-${kind}-label`} style={{ color: '#555', paddingBottom: 6, width: '78%' }}>
        {optionsLoading ? (
          <Select<string>
            aria-label={`${label} (đang tải...)`}
            variant="borderless"
            size="small"
            className="pos-service-fee-select"
            style={{ width: '100%' }}
            placeholder="Đang tải..."
            loading
            disabled
          />
        ) : (
          <Select<string, ServiceSelectOption>
            aria-label={selectLabel}
            variant="borderless"
            size="small"
            allowClear
            className="pos-service-fee-select"
            style={{ width: '100%' }}
            placeholder={placeholderLabel}
            value={local.serviceId ?? undefined}
            options={selectListOptions}
            optionRender={(opt) => buildOptionListLabel(opt.data.name, opt.data.rate)}
            onChange={handleSelectChange}
            // Ô đã chọn (đóng) buộc phải ellipsis theo khung cột hẹp của
            // panel (xem `tableLayout: 'fixed'` ở OrderInfoPanel.tsx), nhưng
            // DANH SÁCH khi MỞ ra không bị ràng buộc đó — nới popup rộng theo
            // nội dung để hiện đủ "Tên (VAT X%)", không cắt chữ như trước.
            popupMatchSelectWidth={false}
            // QC round 4: nới rộng thêm 20% (260px → 312px).
            styles={{ popup: { root: { minWidth: 312 } } }}
          />
        )}
      </td>
      <td style={{ paddingBottom: 6 }}>
        <InputNumber
          aria-label={label}
          className="pos-money-input-right"
          variant="underlined"
          size="small"
          // Stepper của antd nổi đè lên chữ số khi hover/focus — số tiền ở đây
          // căn phải nên bị che mất. Ô tiền không ai bấm mũi tên.
          controls={false}
          style={{ width: '100%' }}
          min={0}
          max={MONEY_MAX}
          value={displayAmount}
          placeholder="Nhập số tiền"
          formatter={moneyFormatter}
          parser={moneyParser}
          onKeyDown={moneyInputKeyDownGuard}
          onChange={handleAmountChange}
        />
      </td>
    </>
  );
}
